"use client"

import { useState } from "react"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { FileText, Search, Download, RefreshCw, Filter, AlertCircle, CheckCircle, XCircle, Clock } from "lucide-react"

export function LogViewerContent() {
  const [searchTerm, setSearchTerm] = useState("")
  const [logLevel, setLogLevel] = useState("all")
  const [timeRange, setTimeRange] = useState("1h")

  // Sample log data
  const logs = [
    {
      id: 1,
      timestamp: new Date(Date.now() - 30000),
      level: "info",
      message: "Bot detection engine initialized successfully",
      ip: "192.168.1.100",
      userAgent: "Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36",
      action: "allow",
      score: 85,
    },
    {
      id: 2,
      timestamp: new Date(Date.now() - 120000),
      level: "warning",
      message: "Suspicious fingerprint detected - multiple requests with same signature",
      ip: "45.123.45.67",
      userAgent: "HeadlessChrome/91.0.4472.124",
      action: "challenge",
      score: 65,
    },
    {
      id: 3,
      timestamp: new Date(Date.now() - 180000),
      level: "error",
      message: "Bot blocked - Puppeteer automation detected",
      ip: "103.45.67.89",
      userAgent: "Mozilla/5.0 (X11; Linux x86_64) AppleWebKit/537.36 HeadlessChrome",
      action: "block",
      score: 15,
    },
    {
      id: 4,
      timestamp: new Date(Date.now() - 240000),
      level: "info",
      message: "Legitimate user verified - passed all security checks",
      ip: "192.168.1.50",
      userAgent: "Mozilla/5.0 (Macintosh; Intel Mac OS X 10_15_7) AppleWebKit/537.36",
      action: "allow",
      score: 95,
    },
    {
      id: 5,
      timestamp: new Date(Date.now() - 300000),
      level: "warning",
      message: "Rate limit exceeded - temporary restriction applied",
      ip: "78.90.12.34",
      userAgent: "curl/7.68.0",
      action: "rate_limit",
      score: 40,
    },
  ]

  const filteredLogs = logs.filter((log) => {
    const matchesSearch =
      log.message.toLowerCase().includes(searchTerm.toLowerCase()) ||
      log.ip.includes(searchTerm) ||
      log.userAgent.toLowerCase().includes(searchTerm.toLowerCase())
    const matchesLevel = logLevel === "all" || log.level === logLevel
    return matchesSearch && matchesLevel
  })

  const getLogLevelColor = (level: string) => {
    switch (level) {
      case "info":
        return "bg-blue-500/20 text-blue-400 border-blue-500/50"
      case "warning":
        return "bg-amber-500/20 text-amber-400 border-amber-500/50"
      case "error":
        return "bg-red-500/20 text-red-400 border-red-500/50"
      default:
        return "bg-slate-500/20 text-slate-400 border-slate-500/50"
    }
  }

  const getActionColor = (action: string) => {
    switch (action) {
      case "allow":
        return "bg-green-500/20 text-green-400 border-green-500/50"
      case "challenge":
        return "bg-amber-500/20 text-amber-400 border-amber-500/50"
      case "block":
        return "bg-red-500/20 text-red-400 border-red-500/50"
      case "rate_limit":
        return "bg-purple-500/20 text-purple-400 border-purple-500/50"
      default:
        return "bg-slate-500/20 text-slate-400 border-slate-500/50"
    }
  }

  const getScoreColor = (score: number) => {
    if (score >= 80) return "text-green-400"
    if (score >= 50) return "text-amber-400"
    return "text-red-400"
  }

  return (
    <>
      {/* Log Viewer Header */}
      <Card className="bg-slate-900/50 border-slate-700/50 backdrop-blur-sm">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-slate-100 flex items-center">
              <FileText className="mr-2 h-5 w-5 text-amber-500" />
              System Log Viewer
            </CardTitle>
            <div className="flex items-center space-x-2">
              <Button variant="ghost" size="icon" className="h-8 w-8">
                <RefreshCw className="h-4 w-4" />
              </Button>
              <Button variant="ghost" size="icon" className="h-8 w-8">
                <Download className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {/* Filters */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                placeholder="Search logs..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 bg-slate-800/50 border-slate-700"
              />
            </div>

            <Select value={logLevel} onValueChange={setLogLevel}>
              <SelectTrigger className="bg-slate-800/50 border-slate-700">
                <SelectValue placeholder="Log Level" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Levels</SelectItem>
                <SelectItem value="info">Info</SelectItem>
                <SelectItem value="warning">Warning</SelectItem>
                <SelectItem value="error">Error</SelectItem>
              </SelectContent>
            </Select>

            <Select value={timeRange} onValueChange={setTimeRange}>
              <SelectTrigger className="bg-slate-800/50 border-slate-700">
                <SelectValue placeholder="Time Range" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="1h">Last Hour</SelectItem>
                <SelectItem value="24h">Last 24 Hours</SelectItem>
                <SelectItem value="7d">Last 7 Days</SelectItem>
                <SelectItem value="30d">Last 30 Days</SelectItem>
              </SelectContent>
            </Select>

            <Button variant="outline" className="border-slate-700 bg-slate-800/50">
              <Filter className="h-4 w-4 mr-2" />
              Advanced Filters
            </Button>
          </div>

          {/* Log Statistics */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-slate-800/50 rounded-lg p-3 border border-slate-700/50">
              <div className="flex items-center justify-between">
                <div className="text-xs text-slate-500">Total Logs</div>
                <FileText className="h-4 w-4 text-slate-400" />
              </div>
              <div className="text-lg font-bold text-slate-200">{filteredLogs.length}</div>
            </div>

            <div className="bg-slate-800/50 rounded-lg p-3 border border-slate-700/50">
              <div className="flex items-center justify-between">
                <div className="text-xs text-slate-500">Errors</div>
                <XCircle className="h-4 w-4 text-red-400" />
              </div>
              <div className="text-lg font-bold text-red-400">
                {filteredLogs.filter((log) => log.level === "error").length}
              </div>
            </div>

            <div className="bg-slate-800/50 rounded-lg p-3 border border-slate-700/50">
              <div className="flex items-center justify-between">
                <div className="text-xs text-slate-500">Warnings</div>
                <AlertCircle className="h-4 w-4 text-amber-400" />
              </div>
              <div className="text-lg font-bold text-amber-400">
                {filteredLogs.filter((log) => log.level === "warning").length}
              </div>
            </div>

            <div className="bg-slate-800/50 rounded-lg p-3 border border-slate-700/50">
              <div className="flex items-center justify-between">
                <div className="text-xs text-slate-500">Info</div>
                <CheckCircle className="h-4 w-4 text-blue-400" />
              </div>
              <div className="text-lg font-bold text-blue-400">
                {filteredLogs.filter((log) => log.level === "info").length}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Log Entries */}
      <Card className="bg-slate-900/50 border-slate-700/50 backdrop-blur-sm">
        <CardHeader>
          <CardTitle className="text-slate-100 text-base">Log Entries ({filteredLogs.length} results)</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {filteredLogs.map((log) => (
              <div
                key={log.id}
                className="bg-slate-800/50 rounded-lg p-4 border border-slate-700/50 hover:bg-slate-800/70 transition-colors"
              >
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center space-x-3">
                    <Badge className={`${getLogLevelColor(log.level)} text-xs`}>{log.level.toUpperCase()}</Badge>
                    <Badge className={`${getActionColor(log.action)} text-xs`}>
                      {log.action.replace("_", " ").toUpperCase()}
                    </Badge>
                    <div className="flex items-center text-xs text-slate-500">
                      <Clock className="h-3 w-3 mr-1" />
                      {log.timestamp.toLocaleTimeString()}
                    </div>
                  </div>
                  <div className={`text-sm font-bold ${getScoreColor(log.score)}`}>Score: {log.score}</div>
                </div>

                <div className="mb-3">
                  <div className="text-sm text-slate-200 font-medium mb-1">{log.message}</div>
                  <div className="text-xs text-slate-400">IP: {log.ip}</div>
                </div>

                <div className="bg-slate-900/50 rounded p-2 border border-slate-700/30">
                  <div className="text-xs text-slate-500 mb-1">User Agent:</div>
                  <div className="text-xs font-mono text-slate-300 break-all">{log.userAgent}</div>
                </div>
              </div>
            ))}
          </div>

          {filteredLogs.length === 0 && (
            <div className="text-center py-8">
              <FileText className="h-12 w-12 text-slate-600 mx-auto mb-4" />
              <div className="text-slate-400">No logs found matching your criteria</div>
            </div>
          )}
        </CardContent>
      </Card>
    </>
  )
}
